import { GAME_CONFIG } from "./config";

/**
 * Calcula la tolerancia proporcional basada en el tamaño de las casillas
 * @param {DOMRect} gridRect - Rectángulo del grid
 * @param {number} gridSize - Número de casillas por fila/columna
 * @returns {number} Tolerancia en píxeles
 */
export function calculateProportionalTolerance(gridRect, gridSize) {
  if (!gridRect || !gridSize) return 30; 

  const tileWidth = gridRect.width / gridSize;
  const tileHeight = gridRect.height / gridSize;
  const averageTileSize = (tileWidth + tileHeight) / 2;
  return averageTileSize * GAME_CONFIG.SELECTION_TOLERANCE_FACTOR;
}

/**
 * Calcula la tolerancia mínima recomendada (para evitar que sea muy pequeña)
 * @param {number} proportionalTolerance - Tolerancia proporcional calculada
 * @returns {number} Tolerancia final con mínimo garantizado
 */
export function getEffectiveTolerance(proportionalTolerance) {
  const MIN_TOLERANCE = 20;
  const MAX_TOLERANCE = 80;

  return Math.max(MIN_TOLERANCE, Math.min(MAX_TOLERANCE, proportionalTolerance));
}

/**
 * Calcula diferentes zonas de tolerancia para el sistema de huellas
 * @param {DOMRect} gridRect - Rectángulo del grid
 * @param {number} gridSize - Número de casillas por fila/columna
 * @returns {Object} Objeto con diferentes zonas de tolerancia
 */
export function calculateToleranceZones(gridRect, gridSize) {
  const baseTolerance = getEffectiveTolerance(
    calculateProportionalTolerance(gridRect, gridSize)
  );

  const HORIZONTAL_FACTOR = GAME_CONFIG.TOLERANCE_DIRECTION_FACTORS.HORIZONTAL;
  const VERTICAL_FACTOR = GAME_CONFIG.TOLERANCE_DIRECTION_FACTORS.VERTICAL;

  return {
    normal: {
      horizontal: baseTolerance * 0.8 * HORIZONTAL_FACTOR,
      vertical: baseTolerance * 0.8 * VERTICAL_FACTOR
    },
    grace: {
      horizontal: baseTolerance * 1.2 * HORIZONTAL_FACTOR,
      vertical: baseTolerance * 1.2 * VERTICAL_FACTOR
    },
    elimination: {
      horizontal: baseTolerance * 1.8 * HORIZONTAL_FACTOR,
      vertical: baseTolerance * 1.8 * VERTICAL_FACTOR
    }
  };
}

/**
 * Verifica en qué zona de tolerancia se encuentra un punto
 * @param {number} x - Coordenada X relativa al grid
 * @param {number} y - Coordenada Y relativa al grid
 * @param {DOMRect} gridRect - Rectángulo del grid
 * @param {Object} toleranceZones - Zonas de tolerancia calculadas con horizontal/vertical
 * @returns {string} "inside", "normal", "grace", "elimination", o "outside"
 */
export function getToleranceZone(x, y, gridRect, toleranceZones) {
  if (x >= 0 && y >= 0 && x <= gridRect.width && y <= gridRect.height) {
    return "inside";
  }

  const distanceLeft = x < 0 ? Math.abs(x) : 0;
  const distanceRight = x > gridRect.width ? x - gridRect.width : 0;
  const distanceTop = y < 0 ? Math.abs(y) : 0;
  const distanceBottom = y > gridRect.height ? y - gridRect.height : 0;

  const horizontalDistance = Math.max(distanceLeft, distanceRight);
  const verticalDistance = Math.max(distanceTop, distanceBottom);

  if (horizontalDistance <= toleranceZones.normal.horizontal &&
    verticalDistance <= toleranceZones.normal.vertical) {
    return "normal";
  }

  if (horizontalDistance <= toleranceZones.grace.horizontal &&
    verticalDistance <= toleranceZones.grace.vertical) {
    return "grace";
  }

  if (horizontalDistance <= toleranceZones.elimination.horizontal &&
    verticalDistance <= toleranceZones.elimination.vertical) {
    return "elimination";
  }

  return "outside";
}

/**
 * Ajusta un punto para mantenerlo dentro de los límites de la zona de gracia
 * @param {number} x - Coordenada X relativa al grid
 * @param {number} y - Coordenada Y relativa al grid
 * @param {DOMRect} gridRect - Rectángulo del grid
 * @param {Object} toleranceZones - Zonas de tolerancia calculadas con horizontal/vertical
 * @returns {Object} Coordenadas ajustadas {x, y}
 */
export function constrainToGraceZone(x, y, gridRect, toleranceZones) {
  if (!isFinite(x) || !isFinite(y)) {
    return { x: 0, y: 0 };
  }

  if (!gridRect || !toleranceZones?.grace) {
    return { x, y };
  }

  const minX = -toleranceZones.grace.horizontal;
  const maxX = gridRect.width + toleranceZones.grace.horizontal;
  const minY = -toleranceZones.grace.vertical;
  const maxY = gridRect.height + toleranceZones.grace.vertical;

  if (!isFinite(minX) || !isFinite(maxX) || !isFinite(minY) || !isFinite(maxY)) {
    return { x, y };
  }

  const constrainedX = Math.max(minX, Math.min(maxX, x));
  const constrainedY = Math.max(minY, Math.min(maxY, y));

  return {
    x: isFinite(constrainedX) ? constrainedX : x,
    y: isFinite(constrainedY) ? constrainedY : y
  };
}
